import { useCallback, useMemo, useState, type PropsWithChildren } from "react";

import { ImportToasterContext } from "./ImportToasterContext";
import type { State, Toast, TrayState } from "./types";

let baseId = 1;
const nextId = () => ++baseId;

export const ImportToasterProvider = ({ children }: PropsWithChildren) => {
  const [state, setState] = useState<State>("hidden");
  const [trayState, setTrayState] = useState<TrayState>("closed");
  const [toasts, setActiveToasts] = useState<Toast[]>([]);

  const setToasts = useCallback((activeToasts: Omit<Toast, "id">[]) => {
    setActiveToasts(activeToasts.map((toast) => ({ ...toast, id: nextId() })));
    setTrayState("open");
    setState("showing");
  }, []);

  const dismissToast = useCallback((id: Toast["id"]) => {
    setActiveToasts((prev) => {
      const updated = prev.filter((toast) => toast.id !== id);

      if (updated.length === 0) {
        setTrayState("closed");
      }

      return updated;
    });
  }, []);

  const show = useCallback(() => setState("showing"), []);
  const hide = useCallback(() => setState("hidden"), []);

  const openTray = useCallback(() => setTrayState("open"), []);
  const closeTray = useCallback(() => setTrayState("closed"), []);
  const toggleTray = useCallback(() => setTrayState((prev) => (prev === "open" ? "closed" : "open")), []);

  const contextValue = useMemo(
    () => ({ state, toasts, setToasts, dismissToast, show, hide, openTray, closeTray, toggleTray, trayState }),
    [state, toasts, setToasts, dismissToast, show, hide, openTray, closeTray, toggleTray, trayState],
  );

  return <ImportToasterContext.Provider value={contextValue}>{children}</ImportToasterContext.Provider>;
};
